import React, { useMemo } from "react";
import { Button, Card, Badge } from "../components/ui";

const formatStatus = (status) =>
  String(status || "draft")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const statusTone = (status) => {
  if (status === "published") return "published";
  if (status === "approved") return "approved";
  if (status === "in_review") return "review";
  if (status === "draft") return "draft";
  return "default";
};

const safeDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not available" : date.toLocaleDateString();
};

export default function ExecutiveDashboardPage({
  analytics = {},
  reports = [],
  auditLogs = [],
  user,
  onNavigate,
}) {
  const reportStats = useMemo(() => {
    const list = Array.isArray(reports) ? reports : [];

    return {
      inReview: list.filter((report) => report.reviewStatus === "in_review").length,
      approved: list.filter((report) => report.reviewStatus === "approved").length,
      published: list.filter((report) => report.reviewStatus === "published").length,
      drafts: list.filter((report) => !report.reviewStatus || report.reviewStatus === "draft").length,
    };
  }, [reports]);

  const recentReports = useMemo(
    () =>
      [...(Array.isArray(reports) ? reports : [])]
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime()
        )
        .slice(0, 5),
    [reports]
  );

  const recentActivity = useMemo(
    () => (Array.isArray(auditLogs) ? auditLogs.slice(0, 6) : []),
    [auditLogs]
  );

  const firstName =
    user?.firstName ||
    user?.name?.split(" ")?.[0] ||
    user?.email?.split("@")?.[0] ||
    "there";

  const cards = [
    {
      label: "Total reports",
      value: analytics.totalReports ?? reports.length ?? 0,
      note: `${reportStats.published} published`,
    },
    {
      label: "Average ESG score",
      value: `${Math.round(Number(analytics.averageScore || 0))}%`,
      note: "Across all reports",
    },
    {
      label: "Awaiting review",
      value: reportStats.inReview,
      note: `${reportStats.approved} approved`,
    },
    {
      label: "High-risk reports",
      value: analytics.highRisk || 0,
      note: "Requires attention",
    },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div>
          <div style={styles.eyebrow}>Executive command center</div>
          <h1 style={styles.title}>Welcome back, {firstName}</h1>
          <p style={styles.subtitle}>
            Monitor ESG reporting, review readiness, risk exposure, and recent platform activity from one workspace.
          </p>
        </div>

        <div style={styles.actions}>
          <Button variant="secondary" onClick={() => onNavigate?.("Document Intelligence")}>
            Upload document
          </Button>
          <Button variant="primary" onClick={() => onNavigate?.("Reports")}>
            Open reports
          </Button>
        </div>
      </div>

      <div style={styles.kpiGrid}>
        {cards.map((card) => (
          <Card key={card.label} style={styles.kpiCard}>
            <div style={styles.kpiLabel}>{card.label}</div>
            <div style={styles.kpiValue}>{card.value}</div>
            <div style={styles.kpiNote}>{card.note}</div>
          </Card>
        ))}
      </div>

      <div style={styles.mainGrid}>
        <Card style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Reporting workflow</h2>
              <p style={styles.panelSubtitle}>Current report distribution by lifecycle status.</p>
            </div>
            <Button variant="secondary" onClick={() => onNavigate?.("Reports")}>
              View all
            </Button>
          </div>

          <div style={styles.workflowGrid}>
            {[
              ["Draft", reportStats.drafts, "draft"],
              ["In review", reportStats.inReview, "review"],
              ["Approved", reportStats.approved, "approved"],
              ["Published", reportStats.published, "published"],
            ].map(([label, value, tone]) => (
              <div key={label} style={styles.workflowItem}>
                <Badge tone={tone}>{label}</Badge>
                <strong style={styles.workflowValue}>{value}</strong>
              </div>
            ))}
          </div>
        </Card>

        <Card style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Risk overview</h2>
              <p style={styles.panelSubtitle}>Portfolio distribution from the latest ESG scores.</p>
            </div>
          </div>

          <RiskRow label="Low risk" value={analytics.lowRisk || 0} total={analytics.totalReports || reports.length} color="#0f766e" />
          <RiskRow label="Medium risk" value={analytics.moderateRisk || 0} total={analytics.totalReports || reports.length} color="#d97706" />
          <RiskRow label="High risk" value={analytics.highRisk || 0} total={analytics.totalReports || reports.length} color="#dc2626" />
        </Card>
      </div>

      <div style={styles.mainGrid}>
        <Card style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Recent reports</h2>
              <p style={styles.panelSubtitle}>Latest reporting records and their current status.</p>
            </div>
          </div>

          {recentReports.length === 0 ? (
            <EmptyMessage
              title="No reports yet"
              description="Create your first ESG report to start tracking disclosure readiness."
              action="Create report"
              onClick={() => onNavigate?.("Reports")}
            />
          ) : (
            <div style={styles.list}>
              {recentReports.map((report) => (
                <div key={report._id || report.id} style={styles.listRow}>
                  <div style={{ minWidth: 0 }}>
                    <div style={styles.itemTitle}>{report.companyName || "Untitled report"}</div>
                    <div style={styles.itemMeta}>
                      {report.reportingYear || "Year not set"} · Updated {safeDate(report.updatedAt || report.createdAt)}
                    </div>
                  </div>
                  <Badge tone={statusTone(report.reviewStatus)}>
                    {formatStatus(report.reviewStatus)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Recent activity</h2>
              <p style={styles.panelSubtitle}>Latest workflow and governance events.</p>
            </div>
          </div>

          {recentActivity.length === 0 ? (
            <EmptyMessage
              title="No activity yet"
              description="Platform events will appear here as your team works in Ecovanta."
            />
          ) : (
            <div style={styles.timeline}>
              {recentActivity.map((log, index) => (
                <div key={log.id || log._id || index} style={styles.timelineItem}>
                  <div style={styles.timelineDot} />
                  <div>
                    <div style={styles.itemTitle}>{log.action || "Platform activity"}</div>
                    <div style={styles.itemMeta}>{log.details || "No additional details"}</div>
                    <div style={styles.timeText}>
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Quick actions</h2>
            <p style={styles.panelSubtitle}>Continue the most common reporting and assurance tasks.</p>
          </div>
        </div>

        <div style={styles.quickGrid}>
          <QuickAction title="Analyse documents" description="Upload and process ESG evidence." onClick={() => onNavigate?.("Document Intelligence")} />
          <QuickAction title="Review evidence" description="Manage evidence lifecycle and approvals." onClick={() => onNavigate?.("Evidence Library")} />
          <QuickAction title="Open materiality" description="Assess impact and financial materiality." onClick={() => onNavigate?.("Materiality")} />
          <QuickAction title="View analytics" description="Explore ESG performance and benchmarks." onClick={() => onNavigate?.("Analytics")} />
        </div>
      </Card>
    </div>
  );
}

function RiskRow({ label, value, total, color }) {
  const denominator = Number(total || 0);
  const percentage = denominator > 0 ? Math.min(100, Math.round((Number(value || 0) / denominator) * 100)) : 0;

  return (
    <div style={styles.riskRow}>
      <div style={styles.riskTop}>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div style={styles.track}>
        <div style={{ ...styles.fill, width: `${percentage}%`, background: color }} />
      </div>
    </div>
  );
}

function QuickAction({ title, description, onClick }) {
  return (
    <button type="button" onClick={onClick} style={styles.quickAction}>
      <strong style={styles.quickTitle}>{title}</strong>
      <span style={styles.quickDescription}>{description}</span>
      <span style={styles.quickLink}>Open →</span>
    </button>
  );
}

function EmptyMessage({ title, description, action, onClick }) {
  return (
    <div style={styles.empty}>
      <strong style={styles.emptyTitle}>{title}</strong>
      <span style={styles.emptyDescription}>{description}</span>
      {action ? (
        <Button variant="secondary" onClick={onClick} style={{ marginTop: 12 }}>
          {action}
        </Button>
      ) : null}
    </div>
  );
}

const styles = {
  page: { display: "grid", gap: 20 },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: 24,
    alignItems: "flex-start",
    padding: "24px",
    borderRadius: 16,
    border: "1px solid #dbe5e1",
    background: "linear-gradient(135deg, #f0fdfa 0%, #ffffff 65%)",
  },
  eyebrow: { color: "#0f766e", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" },
  title: { margin: "8px 0", fontSize: 30, color: "#0f172a" },
  subtitle: { margin: 0, maxWidth: 720, color: "#64748b", lineHeight: 1.6 },
  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 },
  kpiCard: { padding: 18 },
  kpiLabel: { color: "#64748b", fontSize: 13, fontWeight: 700 },
  kpiValue: { marginTop: 8, fontSize: 30, lineHeight: 1, fontWeight: 800, color: "#0f172a" },
  kpiNote: { marginTop: 9, color: "#94a3b8", fontSize: 12 },
  mainGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 },
  panel: { padding: 20 },
  panelHeader: { display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 18 },
  panelTitle: { margin: 0, color: "#0f172a", fontSize: 18 },
  panelSubtitle: { margin: "5px 0 0", color: "#64748b", fontSize: 13, lineHeight: 1.5 },
  workflowGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 },
  workflowItem: { padding: 14, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, display: "grid", gap: 10 },
  workflowValue: { fontSize: 24, color: "#0f172a" },
  riskRow: { marginBottom: 16 },
  riskTop: { display: "flex", justifyContent: "space-between", color: "#475569", fontSize: 13, marginBottom: 7 },
  track: { height: 8, borderRadius: 999, background: "#e2e8f0", overflow: "hidden" },
  fill: { height: "100%", borderRadius: 999, transition: "width 250ms ease" },
  list: { display: "grid" },
  listRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "13px 0", borderBottom: "1px solid #eef2f7" },
  itemTitle: { color: "#0f172a", fontWeight: 700, lineHeight: 1.4 },
  itemMeta: { marginTop: 4, color: "#64748b", fontSize: 12, lineHeight: 1.5 },
  timeText: { marginTop: 5, color: "#94a3b8", fontSize: 11 },
  timeline: { display: "grid", gap: 16 },
  timelineItem: { display: "grid", gridTemplateColumns: "12px 1fr", gap: 10 },
  timelineDot: { width: 9, height: 9, borderRadius: "50%", marginTop: 5, background: "#0f766e", boxShadow: "0 0 0 4px #ccfbf1" },
  quickGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 },
  quickAction: { textAlign: "left", padding: 16, border: "1px solid #dbe5e1", borderRadius: 12, background: "#ffffff", cursor: "pointer", display: "grid", gap: 7, fontFamily: "inherit" },
  quickTitle: { color: "#0f172a", fontSize: 14 },
  quickDescription: { color: "#64748b", fontSize: 12, lineHeight: 1.5 },
  quickLink: { color: "#0f766e", fontSize: 12, fontWeight: 800 },
  empty: { minHeight: 155, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 20, background: "#f8fafc", borderRadius: 12 },
  emptyTitle: { color: "#0f172a", marginBottom: 6 },
  emptyDescription: { maxWidth: 360, color: "#64748b", fontSize: 13, lineHeight: 1.5 },
};
